import * as React from 'react';
import { UserContext } from '../../../Context/UserContext';
import type { FileUploadResponse } from '../../../../MedicareProAPI/API/WebAPI';
import { WebAPI } from '../../../../MedicareProAPI/API/WebAPI';

/**
 * Props interface for the FileUpload component
 */
export interface FileUploadProps {
  /** Callback function triggered when a file is successfully uploaded */
  onChange?: (value: FileUploadResponse) => void;

  /** Current file value/name to display */
  value?: string;

  /** Whether to automatically upload file on selection (default: true) */
  autoUpload?: boolean;

  /** Label text for the upload component */
  label?: string;

  /** Array of allowed file extensions (default: ['pdf', 'docx']) */
  extensions?: string[];

  /** S3 bucket name for file storage */
  bucket: string;

  /** Storage path within the bucket */
  path: string;

  /** Optional key for the component */
  key?: string;

  /** Whether the component can be reused after upload (default: false) */
  reusable?: boolean;

  /** Label for the file selection button (default: 'Select File') */
  fileSelectButtonLabel?: string;

  /** Label for the upload button (default: 'Upload') */
  fileUploadButtonLabel?: string;

  /** Bootstrap color class for select button (default: 'secondary') */
  selectButtonColor?: string;

  /** Bootstrap color class for upload button (default: 'info') */
  uploadButtonColor?: string;

  /** Button style type: 'button' or 'badge' (default: 'button') */
  buttonStyle?: string;

  /** Whether to display component inline (default: false) */
  inline?: boolean;

  userAccessToken?: string;
}

/**
 * FileUpload Component
 *
 * A reusable file upload component that supports both automatic and manual upload modes.
 * Files are uploaded to AWS S3 with configurable bucket and path settings.
 *
 * @param props - The FileUpload component props
 * @returns JSX.Element - The rendered file upload component
 */
export const FileUpload = ({
  onChange,
  value,
  autoUpload = true,
  label = 'Upload File',
  extensions = ['pdf', 'docx'],
  key = '',
  reusable = false,
  fileSelectButtonLabel = 'Select File',
  fileUploadButtonLabel = 'Upload',
  selectButtonColor = 'secondary',
  uploadButtonColor = 'info',
  buttonStyle = 'button',
  inline = false,
  bucket,
  path,
  userAccessToken,
}: FileUploadProps): React.JSX.Element => {
  // Get user context for authentication
  const userData = React.useContext(UserContext);

  // State for upload progress
  const [uploading, setUploading] = React.useState<boolean>(false);

  // State for default value and selected file
  const [defaultValue, setDefaultValue] = React.useState<string>(value);
  const [file, setFile] = React.useState<File>(null);

  // State for error messages
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  // Dynamic CSS classes based on button style and colors
  const baseClass = buttonStyle === 'badge' ? 'badge' : 'btn';
  const selectClass = `${baseClass} ${baseClass}-${selectButtonColor} mb-2 mr-2 cp text-light`;
  const uploadClass = `${baseClass} ${baseClass}-${uploadButtonColor} mb-2 cp`;
  const inlineClass = inline === true ? 'd-inline-block' : '';

  // Initialize WebAPI with user access token
  const webAPI = new WebAPI();
  webAPI.setAccessToken(userAccessToken || userData.userAccessToken);
  /**
   * Handles the file upload process
   *
   * @param selectedFile - The file to upload
   */
  const handleFileUpload = (selectedFile: File) => {
    setUploading(true);

    // Create FormData for file upload
    const data = new FormData();
    data.append('file', selectedFile);

    // Configure file upload properties
    const fileProperties = {
      extensions,
      bucket,
      path,
    };

    // Upload file via WebAPI
    webAPI
      .uploadFile(data, fileProperties)
      .then((fileResponse: FileUploadResponse) => {
        // Update default value if not reusable
        if (!reusable) {
          setDefaultValue(fileResponse.fileName);
        }

        // Trigger onChange callback if provided
        if (onChange) {
          onChange(fileResponse);
        }

        // Clear selected file
        setFile(null);
      })
      .catch((error) => {
        // Set error message on upload failure
        setErrorMessage(error.message);
      })
      .finally(() => {
        // Reset uploading state
        setUploading(false);
      });
  };

  /**
   * Handles file input change event
   *
   * @param e - The file input change event
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Get the selected file (multiple files not supported)
    const selectedFile = e.target.files[0];

    // Clear any previous error messages
    setErrorMessage('');

    if (autoUpload === true) {
      // Upload immediately if auto-upload is enabled
      handleFileUpload(selectedFile);
    } else {
      // Store file for manual upload
      setFile(selectedFile);
    }
  };

  return (
    <div className={inlineClass}>
      {/* Show upload interface when no file is selected/uploaded */}
      {!value && (
        <>
          {/* File selection button with hidden file input */}
          <label className={selectClass} style={{ position: 'relative', cursor: 'pointer' }}>
            <input
              type="file"
              disabled={uploading}
              className="position-absolute"
              style={{
                opacity: 0,
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              }}
              onChange={handleFileInputChange}
            />
            {autoUpload ? (
              // Auto-upload mode: show upload status
              uploading ? (
                <i className="fas fa-spinner fa-spin" />
              ) : (
                <>
                  <i className="far fa-plus-circle mr-1" />
                  {fileUploadButtonLabel || 'Upload File'}
                </>
              )
            ) : (
              // Manual upload mode: show select button
              <>
                <i className="far fa-plus-circle mr-1" />
                {fileSelectButtonLabel || 'Select File'}
              </>
            )}
          </label>

          {/* Manual upload button (only shown in manual mode) */}
          {!autoUpload && (
            <span
              className={uploadClass}
              style={{
                cursor: file ? 'pointer' : 'not-allowed',
                opacity: file ? 1 : 0.5,
              }}
              onClick={() => file && handleFileUpload(file)}
            >
              {uploading ? (
                <i className="fas fa-spinner fa-spin" />
              ) : (
                <>
                  <i className="far fa-upload mr-1" />
                  {fileUploadButtonLabel || 'Upload File'}
                </>
              )}
            </span>
          )}
        </>
      )}

      {/* Error message display */}
      {errorMessage !== '' && (
        <span>
          <small className="text-danger">
            {errorMessage}, use {extensions.join(',')}
          </small>
        </span>
      )}

      {/* Success message showing uploaded file name */}
      {value && <p className="alert alert-info small">{value}</p>}
    </div>
  );
};
